import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  Divider,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  BugReport as BugReportIcon,
  Message as MessageIcon,
  AttachFile as AttachFileIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import axiosInstance from '../api/axiosInstance';
import socketService from '../services/socketService';
import { getMessages } from '../api/chatApi';
import { Message } from '../models/message';
import { uploadWorkerFile, FileType } from '../api/workerFilesApi';
import { uploadCert, ingestCertFromWorkerFile } from '../api/workerCertificationsApi';

interface WorkerOption {
  workerId: string;
  label: string;
  phoneNumber?: string;
  status?: string;
  professionKey?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`debug-tabpanel-${index}`}
      aria-labelledby={`debug-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DebugQueuePage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // Worker selection
  const [workers, setWorkers] = useState<WorkerOption[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<WorkerOption | null>(null);
  const [useNewPhone, setUseNewPhone] = useState(false);

  // Test incoming message state
  const [workerId, setWorkerId] = useState('');
  const [message, setMessage] = useState('');

  // Fetch workers on mount
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const response = await axiosInstance.post('/workers/search', {}, { params: { page: 1, limit: 100 } });
        const workerList: WorkerOption[] = (response.data.workers || []).map((w: any) => ({
          workerId: w.workerId,
          label: `${w.firstName || ''} ${w.lastName || ''} (${w.workerId})`.trim(),
          phoneNumber: w.phoneNumber,
          status: w.status,
          professionKey: w.professionKey,
        }));
        setWorkers(workerList);
      } catch (err) {
        console.error('Failed to fetch workers for debug page:', err);
      }
    };
    fetchWorkers();
  }, []);

  // Subscribe to v7 cert ingest Socket.IO events — completed / failed /
  // sourceUpdated. The backend's ChatGateway joins every connected admin
  // client to ADMIN_CERT_QUEUE_ROOM, so we get every cert job's outcome.
  useEffect(() => {
    socketService.connect();
    const onCompleted = (payload: any) => {
      setCertEvents(prev => [{
        kind: 'completed', at: new Date().toISOString(),
        jobId: payload?.jobId, workerId: payload?.workerId,
        certIds: payload?.certIds ?? [], failedCertIds: payload?.failedCertIds ?? [],
      } satisfies CertEvent, ...prev].slice(0, 20));
    };
    const onFailed = (payload: any) => {
      setCertEvents(prev => [{
        kind: 'failed', at: new Date().toISOString(),
        jobId: payload?.jobId, workerId: payload?.workerId,
        reason: payload?.reason, errorCode: payload?.errorCode,
      } satisfies CertEvent, ...prev].slice(0, 20));
    };
    const onSourceUpdated = (payload: any) => {
      setCertEvents(prev => [{
        kind: 'sourceUpdated', at: new Date().toISOString(),
        certId: payload?.certId, workerId: payload?.workerId,
        extractionSucceeded: !!payload?.extractionSucceeded,
        photoMismatch: !!payload?.photoMismatch,
      } satisfies CertEvent, ...prev].slice(0, 20));
    };
    socketService.onCertUploadCompleted(onCompleted);
    socketService.onCertUploadFailed(onFailed);
    socketService.onCertSourceUpdated(onSourceUpdated);
    return () => {
      socketService.offCertUploadCompleted(onCompleted);
      socketService.offCertUploadFailed(onFailed);
      socketService.offCertSourceUpdated(onSourceUpdated);
    };
  }, []);

  // Live chat preview — fetch conversation history + stream newMessage events.
  // Same plumbing as ChatConversation, rendered compact.
  //
  // The workerId we feed getMessages/joinSessionRoom is whichever of these is
  // most specific, in order:
  //   1. selectedWorker.workerId (autocomplete pick)
  //   2. workers[] match against the typed value (existing seeded worker)
  //   3. the typed string itself — for brand-new workers (the test-incoming
  //      endpoint creates a session keyed on the phone number, so the room
  //      name is session_+31xxx). Without this fallback the preview stays
  //      blank for any fresh phone you generate.
  const [previewMessages, setPreviewMessages] = useState<Message[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const effectivePreviewWorkerId = React.useMemo<string>(() => {
    if (selectedWorker?.workerId) return selectedWorker.workerId;
    const needle = workerId.trim();
    if (!needle) return '';
    const match =
      workers.find(w => w.workerId === needle) ??
      workers.find(w => w.phoneNumber === needle);
    return match?.workerId ?? needle;
  }, [selectedWorker, workerId, workers]);
  const previewLabel: string | null = React.useMemo(() => {
    if (selectedWorker) return selectedWorker.label;
    if (!effectivePreviewWorkerId) return null;
    const match = workers.find(w => w.workerId === effectivePreviewWorkerId);
    return match?.label ?? effectivePreviewWorkerId;
  }, [selectedWorker, effectivePreviewWorkerId, workers]);
  useEffect(() => {
    const wId = effectivePreviewWorkerId;
    if (!wId) {
      setPreviewMessages([]);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    let mounted = true;
    getMessages(wId)
      .then(msgs => {
        if (!mounted) return;
        setPreviewMessages(
          [...msgs].sort((a, b) =>
            new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime()),
        );
      })
      .catch(err => {
        if (!mounted) return;
        setPreviewMessages([]);
        setPreviewError(
          err?.response?.status === 404
            ? 'No session found for this workerId yet — send a message to create one.'
            : err?.response?.data?.message ?? err?.message ?? 'Failed to load messages',
        );
      })
      .finally(() => mounted && setPreviewLoading(false));

    socketService.connect();
    const handler = (m: unknown) => {
      const msg = m as Message;
      if (String(msg.workerId) !== String(wId)) return;
      setPreviewMessages(prev =>
        prev.some(p => p.id === msg.id) ? prev : [...prev, msg],
      );
    };
    socketService.joinSessionRoom(wId);
    socketService.onNewMessage(handler);
    return () => {
      mounted = false;
      socketService.leaveSessionRoom(wId);
      socketService.offNewMessage(handler);
    };
  }, [effectivePreviewWorkerId]);

  // Test file upload state
  const [fileWorkerId, setFileWorkerId] = useState('');
  const [fileName, setFileName] = useState('test-cv.pdf');
  const [mimeType, setMimeType] = useState('application/pdf');
  const [caption, setCaption] = useState('');
  const [base64Content, setBase64Content] = useState('');
  const [pickedFileSize, setPickedFileSize] = useState<number | null>(null);
  const [pickedFileBlob, setPickedFileBlob] = useState<File | null>(null);
  const filePickerRef = React.useRef<HTMLInputElement>(null);
  // Which backend route to hit when "Send Test File" is clicked.
  //   whatsapp → existing /whatsapp/test-file-upload (bot's FSM decides routing)
  //   cert     → POST /worker-certifications/upload (direct v7 ingest)
  //   cv       → POST /worker-files/upload with fileType=CV (no cert ingest)
  type RouteAs = 'whatsapp' | 'cert' | 'cv';
  const [routeAs, setRouteAs] = useState<RouteAs>('whatsapp');

  // Cert ingest live feedback — listens for cert_upload_completed / failed
  // emitted by the v7 Bull worker so the harness shows the result inline.
  type CertEvent =
    | { kind: 'completed'; jobId: string; workerId: number; certIds: string[]; failedCertIds: string[]; at: string }
    | { kind: 'failed'; jobId: string; workerId: number; reason: string; errorCode?: string; at: string }
    | { kind: 'sourceUpdated'; certId: string; workerId: number; extractionSucceeded: boolean; photoMismatch?: boolean; at: string };
  const [certEvents, setCertEvents] = useState<CertEvent[]>([]);
  
  // Admin message state
  const [adminWorkerId, setAdminWorkerId] = useState('');
  const [adminMessage, setAdminMessage] = useState('');
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  // Response state
  const [lastResponse, setLastResponse] = useState<any>(null);

  // Load test PDF file
  const loadTestPDF = async () => {
    try {
      console.log('Attempting to load test PDF from /cv_test.pdf');
      const response = await fetch('/cv_test.pdf');
      
      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      console.log('PDF loaded, size:', arrayBuffer.byteLength, 'bytes');
      
      if (arrayBuffer.byteLength < 1000) {
        console.warn('PDF seems too small, might be corrupted or empty');
      }
      
      // Convert ArrayBuffer to base64 more efficiently
      const uint8Array = new Uint8Array(arrayBuffer);
      let binary = '';
      const len = uint8Array.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(uint8Array[i]);
      }
      const base64 = btoa(binary);
      console.log('Base64 length:', base64.length);
      console.log('Base64 preview:', base64.substring(0, 100) + '...');
      
      setBase64Content(base64);
      setFileName('cv_test.pdf');
      setMimeType('application/pdf');
      
      // Return success info instead of calling showSnackbar
      return { success: true, size: arrayBuffer.byteLength };
    } catch (error) {
      console.error('Error loading test PDF:', error);
      throw error; // Re-throw so the calling function knows it failed
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copied to clipboard!', 'success');
  };

  // Test incoming message
  const handleTestIncomingMessage = async () => {
    if (!workerId || !message) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post('/whatsapp/test-incoming', {
        phoneNumber: workerId,
        message,
      });
      setLastResponse(response.data);
      showSnackbar('Test message sent successfully!', 'success');
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to send test message', 'error');
      setLastResponse(error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Pick a real file from disk → autofill fileName, mimeType, base64Content.
  // Strips the "data:...;base64," prefix that FileReader.readAsDataURL adds so
  // the backend receives a clean base64 payload.
  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setMimeType(f.type || 'application/octet-stream');
    setPickedFileSize(f.size);
    setPickedFileBlob(f); // keep the blob for multipart dashboard uploads
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const commaIdx = result.indexOf(',');
      setBase64Content(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      showSnackbar(`Loaded ${f.name} (${(f.size / 1024).toFixed(0)} KB)`, 'success');
    };
    reader.onerror = () => showSnackbar('Failed to read file', 'error');
    reader.readAsDataURL(f);
  };

  // Test file upload — branches by routeAs:
  //   whatsapp → simulates WhatsApp incoming (bot's FSM decides CV vs cert vs other)
  //   cert     → POST /worker-certifications/upload (direct v7 ingest, no FSM)
  //   cv       → uploadWorkerFile with fileType=CV (no cert ingest path)
  const handleTestFileUpload = async () => {
    if (!fileWorkerId) {
      showSnackbar('Worker ID is required', 'error');
      return;
    }
    if (!base64Content && routeAs === 'whatsapp') {
      showSnackbar('Pick a file or load the test PDF first', 'error');
      return;
    }
    if (!pickedFileBlob && routeAs !== 'whatsapp') {
      showSnackbar(
        'Dashboard routes need a real File — pick one from disk above.',
        'error',
      );
      return;
    }

    setLoading(true);
    try {
      if (routeAs === 'whatsapp') {
        const response = await axiosInstance.post('/whatsapp/test-file-upload', {
          workerId: fileWorkerId,
          fileName,
          mimeType,
          base64Content: base64Content || 'SGVsbG8gV29ybGQh',
          caption,
        });
        setLastResponse(response.data);
        showSnackbar('WhatsApp file upload simulated — bot will decide routing.', 'success');
      } else if (routeAs === 'cert') {
        // Resolve fileWorkerId (string) to the numeric workers.id the cert
        // endpoint expects. The fileWorkerId is set from the Autocomplete to
        // the worker's `workerId` (a string). The cert /upload endpoint
        // expects the numeric primary key.
        const lookup = workers.find(w => w.workerId === fileWorkerId);
        const numericId = lookup
          ? // workers list doesn't carry id; fall back to a small lookup hit.
            await axiosInstance.get(`/workers/${fileWorkerId}`).then(r => r.data?.id)
          : null;
        if (!numericId) {
          throw new Error(`Could not resolve numeric workerId for "${fileWorkerId}". Pick a worker from the autocomplete.`);
        }
        const result = await uploadCert(numericId, pickedFileBlob!);
        setLastResponse(result);
        if (result.jobId) {
          showSnackbar(`Cert ingest queued (job ${result.jobId}). Watch the events panel →`, 'success');
        } else {
          showSnackbar(`Dedup hit: ${result.dedupedCertIds.length} existing cert(s) returned.`, 'info');
        }
      } else { // cv
        const saved = await uploadWorkerFile({
          workerId: fileWorkerId,
          file: pickedFileBlob!,
          fileType: FileType.CV,
          description: caption || undefined,
        });
        setLastResponse(saved);
        showSnackbar(`CV file uploaded (worker_file id ${saved.id}). No cert ingest triggered.`, 'success');
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message ?? error?.message ?? 'Failed to send file', 'error');
      setLastResponse(error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Send admin message
  const handleSendAdminMessage = async () => {
    if (!adminWorkerId || !adminMessage) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`/sessions/${adminWorkerId}/admin-message`, {
        message: adminMessage,
      });
      setLastResponse(response.data);
      showSnackbar('Admin message sent successfully!', 'success');
      setAdminMessage(''); // Clear message after sending
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to send admin message', 'error');
      setLastResponse(error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Test file parsing
  const handleTestFileParsing = async () => {
    if (!workerId) {
      showSnackbar('Please enter a worker ID', 'error');
      return;
    }

    setLoading(true);
    try {
      console.log('=== DEBUG: Current state ===');
      console.log('- base64Content exists:', !!base64Content);
      console.log('- base64Content length:', base64Content?.length || 0);
      console.log('- base64Content preview:', base64Content?.substring(0, 50) + '...');
      console.log('- fileName:', fileName);
      console.log('- mimeType:', mimeType);

      // Load the test PDF if not already loaded
      if (!base64Content) {
        console.log('No base64Content found, loading test PDF...');
        try {
          const result = await loadTestPDF();
          console.log('loadTestPDF completed successfully');
          showSnackbar(`Test PDF loaded successfully! (${result.size} bytes)`, 'success');
        } catch (loadError) {
          console.error('loadTestPDF failed:', loadError);
          showSnackbar(`Failed to load test PDF: ${loadError instanceof Error ? loadError.message : 'Unknown error'}`, 'warning');
        }
      } else {
        console.log('base64Content already exists, skipping loadTestPDF');
      }

      console.log('=== DEBUG: After loading ===');
      console.log('- base64Content length:', base64Content?.length || 0);
      console.log('- base64Content preview:', base64Content?.substring(0, 50) + '...');

      const response = await axiosInstance.post(`/sessions/worker/${workerId}/test-file-parsing`, {
        fileName: fileName || 'cv_test.pdf',
        mimeType: mimeType || 'application/pdf',
        base64Content: base64Content || 'SGVsbG8gV29ybGQh', // Fallback to "Hello World!"
        fileType: 'CV',
      });
      setLastResponse(response.data);
      showSnackbar('File parsing test initiated!', 'success');
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to test file parsing', 'error');
      setLastResponse(error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateSamplePhoneNumber = () => {
    // Generate a realistic Dutch mobile number for testing
    const num = '+316' + Math.floor(10000000 + Math.random() * 90000000);
    setWorkerId(num);
    setFileWorkerId(num);
    setAdminWorkerId(num);
    showSnackbar('Generated sample Worker ID', 'info');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BugReportIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              Queue Debug Tools
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Test various queue functionalities and message endpoints
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
          <Button
            variant={useNewPhone ? 'contained' : 'outlined'}
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setUseNewPhone(true);
              setSelectedWorker(null);
              generateSamplePhoneNumber();
            }}
            size="small"
          >
            New Phone Number
          </Button>
          <Typography variant="body2" color="text.secondary">or</Typography>
          <Autocomplete
            size="small"
            sx={{ minWidth: 350 }}
            options={workers}
            getOptionLabel={(opt) => opt.label}
            value={selectedWorker}
            onChange={(_e, val) => {
              setSelectedWorker(val);
              if (val) {
                setUseNewPhone(false);
                setWorkerId(val.phoneNumber || val.workerId);
                setFileWorkerId(val.workerId);
                setAdminWorkerId(val.workerId);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.workerId}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{option.label}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {option.status && <Chip label={option.status} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    {option.professionKey && <Chip label={option.professionKey} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    {option.phoneNumber && <Typography variant="caption" color="text.secondary">{option.phoneNumber}</Typography>}
                  </Box>
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Pick existing worker" placeholder="Search by name or ID..." />}
          />
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="debug tabs">
            <Tab label="Incoming Message" icon={<MessageIcon />} iconPosition="start" />
            <Tab label="File Upload" icon={<AttachFileIcon />} iconPosition="start" />
            <Tab label="Admin Message" icon={<SendIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card>
                  <CardHeader title="Test Incoming WhatsApp Message" />
                  <CardContent>
                    <TextField
                      fullWidth
                      label={useNewPhone ? "Phone Number (new worker)" : "Phone Number / Worker ID"}
                      value={workerId}
                      onChange={(e) => { setWorkerId(e.target.value); setSelectedWorker(null); }}
                      margin="normal"
                      placeholder="+31612345678"
                      helperText={
                        selectedWorker
                          ? `Sending as: ${selectedWorker.label} (${selectedWorker.status || 'unknown status'})`
                          : useNewPhone
                          ? "This will create a brand new worker + session"
                          : "Pick a worker above or type a phone number"
                      }
                    />
                    <TextField
                      fullWidth
                      label="Message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      margin="normal"
                      multiline
                      rows={4}
                      helperText="The message content to simulate"
                    />
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleTestIncomingMessage}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                      >
                        Send Test Message
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleTestFileParsing}
                        disabled={loading || !workerId}
                      >
                        Test File Parsing
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={async () => {
                          console.log('Force reloading PDF...');
                          setBase64Content(''); // Clear existing content
                          try {
                            const result = await loadTestPDF();
                            showSnackbar(`Test PDF loaded successfully! (${result.size} bytes)`, 'success');
                          } catch (error) {
                            showSnackbar(`Failed to load test PDF: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
                          }
                        }}
                        disabled={loading}
                        sx={{ ml: 1 }}
                      >
                        Force Reload PDF
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader title="Sample Messages" />
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Quick Templates:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button size="small" onClick={() => setMessage('Hello, I would like to apply for a job')}>
                        Job Application
                      </Button>
                      <Button size="small" onClick={() => setMessage('I am a welder with 5 years of experience')}>
                        Experience Info
                      </Button>
                      <Button size="small" onClick={() => setMessage('My phone number is +31612345678')}>
                        Contact Info
                      </Button>
                      <Button size="small" onClick={() => setMessage('I speak English and Dutch')}>
                        Language Info
                      </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Cert confirmation flow (v7):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button size="small" onClick={() => setMessage('ja')}>
                        "ja" — confirm
                      </Button>
                      <Button size="small" onClick={() => setMessage('nee, het is VCA-VOL niet basis')}>
                        "nee, het is X" — correction
                      </Button>
                      <Button size="small" onClick={() => setMessage('Welding cert, geldig tot juni 2027')}>
                        Typed details
                      </Button>
                      <Button size="small" onClick={() => setMessage('ik weet het niet')}>
                        "ik weet het niet" — skip
                      </Button>
                      <Button size="small" onClick={() => setMessage('klaar')}>
                        "klaar" — finish
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title="Live chat preview"
                  subheader={
                    effectivePreviewWorkerId
                      ? `${previewLabel} · room session_${effectivePreviewWorkerId}`
                      : 'Pick a worker (autocomplete) or type a phone/workerId to stream their conversation'
                  }
                />
                <CardContent>
                  {!effectivePreviewWorkerId ? (
                    <Typography variant="body2" color="text.secondary">
                      No workerId. The preview shows live messages over Socket.IO
                      — same room as <code>/chat</code> — so you can verify bot replies
                      land without leaving this page.
                    </Typography>
                  ) : previewLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : previewError ? (
                    <Alert severity="info" sx={{ fontSize: 13 }}>
                      {previewError}
                    </Alert>
                  ) : previewMessages.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No messages yet. Send a test message on the left — it should appear here.
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: 520, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {previewMessages.slice(-30).map(msg => {
                        const isIncoming = msg.direction === 'INCOMING';
                        const senderColor =
                          msg.senderType === 'AI' ? '#e3f2fd' :
                          msg.senderType === 'ADMIN' ? '#fff3e0' :
                          msg.senderType === 'SYSTEM' ? '#f5f5f5' :
                          '#e8f5e9';
                        return (
                          <Box
                            key={msg.id}
                            sx={{
                              alignSelf: isIncoming ? 'flex-start' : 'flex-end',
                              maxWidth: '85%',
                              p: 1,
                              borderRadius: 1,
                              bgcolor: senderColor,
                              fontFamily: 'system-ui',
                              fontSize: 13,
                            }}
                          >
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }}>
                              {msg.senderType} · {new Date(msg.receivedAt).toLocaleTimeString()}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {msg.message}
                            </Typography>
                            {msg.translatedMessage && msg.translatedMessage !== msg.message && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                                ↳ {msg.translatedMessage}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {effectivePreviewWorkerId && previewMessages.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'right' }}>
                      Showing last {Math.min(30, previewMessages.length)} of {previewMessages.length} message(s) ·
                      live via Socket.IO
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Test File Upload" />
                <CardContent>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="route-as-label">Route as</InputLabel>
                    <Select
                      labelId="route-as-label"
                      value={routeAs}
                      onChange={(e) => setRouteAs(e.target.value as RouteAs)}
                      label="Route as"
                    >
                      <MenuItem value="whatsapp">
                        WhatsApp incoming — bot's FSM decides (default)
                      </MenuItem>
                      <MenuItem value="cert">
                        Dashboard CERT upload — direct v7 ingest pipeline
                      </MenuItem>
                      <MenuItem value="cv">
                        Dashboard CV upload — worker_files only, no cert ingest
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {routeAs === 'whatsapp' && (
                      <>Simulates a worker sending the file via WhatsApp. The bot's
                      session state decides whether it's treated as CV, CERTIFICATION,
                      or other. Watch the chat preview on the Incoming tab for replies.</>
                    )}
                    {routeAs === 'cert' && (
                      <>Hits <code>POST /worker-certifications/upload</code> directly.
                      Goes straight into the v7 Bull pipeline (Gemini split + extract).
                      Result lands in the "Cert ingest events" panel on the right.</>
                    )}
                    {routeAs === 'cv' && (
                      <>Hits <code>POST /worker-files/upload</code> with
                      <code> fileType=CV</code>. No cert ingest is triggered.
                      The file shows up in the worker's Files tab.</>
                    )}
                  </Alert>

                  <input
                    ref={filePickerRef}
                    type="file"
                    hidden
                    onChange={handlePickFile}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => filePickerRef.current?.click()}
                      startIcon={<AttachFileIcon />}
                    >
                      Pick file from disk
                    </Button>
                    {pickedFileSize !== null && (
                      <Chip
                        size="small"
                        label={`${fileName} — ${(pickedFileSize / 1024).toFixed(0)} KB`}
                        onDelete={() => {
                          setFileName('test-cv.pdf');
                          setMimeType('application/pdf');
                          setBase64Content('');
                          setPickedFileSize(null);
                          setPickedFileBlob(null);
                        }}
                      />
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    label="Worker ID / Phone"
                    value={fileWorkerId}
                    onChange={(e) => setFileWorkerId(e.target.value)}
                    margin="normal"
                    helperText="Worker's workerId or phone number (+316…)"
                  />
                  <TextField
                    fullWidth
                    label="File Name"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    margin="normal"
                    helperText="Auto-filled when you pick a file above"
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel>MIME Type</InputLabel>
                    <Select
                      value={mimeType}
                      onChange={(e) => setMimeType(e.target.value)}
                      label="MIME Type"
                    >
                      <MenuItem value="application/pdf">PDF (application/pdf)</MenuItem>
                      <MenuItem value="application/vnd.openxmlformats-officedocument.wordprocessingml.document">
                        DOCX (Word)
                      </MenuItem>
                      <MenuItem value="application/msword">DOC (Word Legacy)</MenuItem>
                      <MenuItem value="image/jpeg">JPEG Image</MenuItem>
                      <MenuItem value="image/png">PNG Image</MenuItem>
                      <MenuItem value="image/heic">HEIC (iPhone)</MenuItem>
                      <MenuItem value="image/webp">WEBP</MenuItem>
                      <MenuItem value="application/octet-stream">octet-stream (iPhone share-sheet)</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Caption (optional)"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    margin="normal"
                    helperText="WhatsApp caption (forwarded to AI context for classification)"
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={loadTestPDF}
                      disabled={loading}
                      startIcon={<AttachFileIcon />}
                    >
                      Load Test PDF
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleTestFileUpload}
                      disabled={loading ||
                        (routeAs === 'whatsapp' ? !base64Content : !pickedFileBlob)}
                      startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                      {routeAs === 'whatsapp' && 'Send as WhatsApp file'}
                      {routeAs === 'cert' && 'Upload as CERTIFICATE'}
                      {routeAs === 'cv' && 'Upload as CV'}
                    </Button>
                  </Box>
                  {base64Content && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                      Payload ready: {(base64Content.length / 1024).toFixed(0)} KB of base64
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title="Cert ingest events (live)"
                  action={
                    certEvents.length > 0 ? (
                      <Button size="small" onClick={() => setCertEvents([])}>
                        Clear
                      </Button>
                    ) : null
                  }
                />
                <CardContent>
                  {certEvents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Waiting for cert_upload_completed / cert_upload_failed / cert_source_updated
                      Socket.IO events. Send a CERTIFICATION file from the left to see the
                      v7 Bull pipeline outcome here.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 480, overflow: 'auto' }}>
                      {certEvents.map((ev, i) => {
                        if (ev.kind === 'completed') {
                          const ok = ev.certIds.length - ev.failedCertIds.length;
                          return (
                            <Alert key={i} severity={ev.failedCertIds.length === 0 ? 'success' : 'warning'} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {ev.at.slice(11, 19)} — job <strong>{ev.jobId}</strong> · worker {ev.workerId}
                              </Typography>
                              completed: {ok} extracted, {ev.failedCertIds.length} extraction_failed
                              ({ev.certIds.length} total cert row(s))
                            </Alert>
                          );
                        }
                        if (ev.kind === 'failed') {
                          return (
                            <Alert key={i} severity="error" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {ev.at.slice(11, 19)} — job <strong>{ev.jobId}</strong> · worker {ev.workerId}
                              </Typography>
                              failed: {ev.errorCode ?? '(no code)'} — {ev.reason}
                            </Alert>
                          );
                        }
                        return (
                          <Alert key={i} severity={ev.photoMismatch ? 'warning' : ev.extractionSucceeded ? 'success' : 'info'} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {ev.at.slice(11, 19)} — cert <strong>{ev.certId}</strong> · worker {ev.workerId}
                            </Typography>
                            source updated · extracted={String(ev.extractionSucceeded)}
                            {ev.photoMismatch ? ' · photo-mismatch' : ''}
                          </Alert>
                        );
                      })}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Send Admin Message to Worker" />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This will send an actual WhatsApp message if WHATSAPP_SEND_BACK is enabled
                  </Alert>
                  <TextField
                    fullWidth
                    label="Worker ID"
                    value={adminWorkerId}
                    onChange={(e) => setAdminWorkerId(e.target.value)}
                    margin="normal"
                    helperText="The worker's UUID"
                  />
                  <TextField
                    fullWidth
                    label="Message"
                    value={adminMessage}
                    onChange={(e) => setAdminMessage(e.target.value)}
                    margin="normal"
                    multiline
                    rows={4}
                    helperText="Message to send to the worker"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendAdminMessage}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{ mt: 2 }}
                  >
                    Send Admin Message
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Admin Message Templates" />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button size="small" onClick={() => setAdminMessage('Hello! How can I help you today?')}>
                      Greeting
                    </Button>
                    <Button size="small" onClick={() => setAdminMessage('Thank you for your application. We will review it and get back to you soon.')}>
                      Application Received
                    </Button>
                    <Button size="small" onClick={() => setAdminMessage('Could you please provide more information about your work experience?')}>
                      Request Info
                    </Button>
                    <Button size="small" onClick={() => setAdminMessage('Please upload your CV in PDF format.')}>
                      Request CV
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {lastResponse && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Last Response</Typography>
              <Tooltip title="Copy response">
                <IconButton onClick={() => copyToClipboard(JSON.stringify(lastResponse, null, 2))}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                {JSON.stringify(lastResponse, null, 2)}
              </pre>
            </Paper>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DebugQueuePage;
